class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    if (facetForm) facetForm.addEventListener('input', this.debouncedOnSubmit);

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    if (FacetFiltersForm.historyListenerSet) return;
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
    FacetFiltersForm.historyListenerSet = true;
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static normalizeSearchParams(searchParams, root = document) {
    const source = new URLSearchParams(searchParams || '');
    const normalized = new URLSearchParams();
    const minInput = root.querySelector?.('input[name="filter.v.price.gte"]');
    const maxInput = root.querySelector?.('input[name="filter.v.price.lte"]');
    const minBoundary = Number(minInput?.getAttribute('min') || 0);
    const maxBoundary = Number(maxInput?.getAttribute('max'));

    source.forEach((rawValue, key) => {
      const value = String(rawValue).trim();
      if (!value) return;
      if (key === 'filter.v.price.gte' && Number(value) <= minBoundary) return;
      if (
        key === 'filter.v.price.lte' &&
        Number.isFinite(maxBoundary) &&
        Number(value) >= maxBoundary
      ) return;
      normalized.append(key, value);
    });

    return normalized.toString();
  }

  static captureUiState(event) {
    const drawer = document.querySelector('menu-drawer.mobile-facets__wrapper');
    const mainDetails = drawer?.mainDetailsToggle || drawer?.querySelector('details');
    const eventTarget = event?.target instanceof Element ? event.target : null;
    const documentActiveElement =
      document.activeElement instanceof Element &&
      document.activeElement !== document.body &&
      document.activeElement !== document.documentElement
        ? document.activeElement
        : null;
    const activeElement = documentActiveElement || eventTarget;
    let focus = null;

    if (activeElement?.id) {
      focus = { id: activeElement.id };
    } else if (activeElement?.getAttribute('name')) {
      focus = {
        name: activeElement.getAttribute('name'),
        value: 'value' in activeElement ? String(activeElement.value) : null,
      };
    }

    return {
      drawerOpen: Boolean(mainDetails?.hasAttribute('open')),
      openFilterIndexes: Array.from(
        drawer?.querySelectorAll('.mobile-facets__details[open]') || [],
      ).map((details) => details.dataset.index).filter(Boolean),
      focus,
    };
  }

  static restoreUiState(state) {
    if (!state) return;
    const drawer = document.querySelector('menu-drawer.mobile-facets__wrapper');
    const mainDetails = drawer?.mainDetailsToggle || drawer?.querySelector('details');
    const drawerLockActive = document.body.classList.contains('mobile-open');

    if (state.drawerOpen && drawerLockActive && drawer && mainDetails) {
      mainDetails.setAttribute('open', '');
      state.openFilterIndexes.forEach((index) => {
        const details = Array.from(
          drawer.querySelectorAll('.mobile-facets__details'),
        ).find((candidate) => candidate.dataset.index === index);
        if (!details) return;
        details.setAttribute('open', '');
        details.classList.add('menu-opening');
        details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
      });

      const summary = mainDetails.querySelector('summary');
      if (summary && typeof drawer.openMenuDrawer === 'function') {
        drawer.openMenuDrawer(summary);
      }
    }

    let focusTarget = null;
    if (state.focus?.id) {
      focusTarget = document.getElementById(state.focus.id);
    } else if (state.focus?.name) {
      focusTarget = Array.from(document.querySelectorAll('[name]')).find(
        (candidate) =>
          candidate.getAttribute('name') === state.focus.name &&
          (state.focus.value === null || String(candidate.value) === state.focus.value),
      );
    }
    focusTarget?.focus({ preventScroll: true });
  }

  static setLoading(loading) {
    document.querySelector('#ProductGridContainer .collection')
      ?.classList.toggle('loading', loading);
    document.getElementById('ProductCount')?.classList.toggle('loading', loading);
    document.getElementById('ProductCountDesktop')?.classList.toggle('loading', loading);
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    searchParams = FacetFiltersForm.normalizeSearchParams(searchParams);
    FacetFiltersForm.searchParamsPrev = searchParams;
    FacetFiltersForm.requestController?.abort();
    FacetFiltersForm.requestController = new AbortController();
    const requestId = ++FacetFiltersForm.requestSequence;
    const uiState = FacetFiltersForm.captureUiState(event);
    const sections = FacetFiltersForm.getSections();
    FacetFiltersForm.setLoading(true);

    sections.forEach((section) => {
      const sectionParams = new URLSearchParams(searchParams);
      sectionParams.set('section_id', section.section);
      const url = `${window.location.pathname}?${sectionParams.toString()}`;
      const filterDataUrl = element => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl) ?
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, requestId, uiState) :
        FacetFiltersForm.renderSectionFromFetch(
          url,
          event,
          requestId,
          uiState,
          FacetFiltersForm.requestController.signal,
          `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`,
        );
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event, requestId, uiState, signal, fallbackURL) {
    fetch(url, { signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Facet request failed (${response.status})`);
        return response.text();
      })
      .then((responseText) => {
        if (requestId !== FacetFiltersForm.requestSequence) return;
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData.filter((entry) => entry.url !== url),
          { html, url },
        ].slice(-20);
        FacetFiltersForm.renderSection(html, event, requestId, uiState);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || requestId !== FacetFiltersForm.requestSequence) return;
        FacetFiltersForm.setLoading(false);
        console.error('Collection filters could not refresh; falling back to navigation.', error);
        window.location.assign(fallbackURL);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, requestId, uiState) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event, requestId, uiState);
  }

  static renderSection(html, event, requestId, uiState) {
    if (requestId !== FacetFiltersForm.requestSequence) return;
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    if (!parsedHTML.getElementById('ProductGridContainer')) {
      throw new Error('Facet response is missing ProductGridContainer');
    }
    FacetFiltersForm.renderProductGridContainer(parsedHTML);
    FacetFiltersForm.renderFilters(parsedHTML, event);
    FacetFiltersForm.renderProductCount(parsedHTML);
    FacetFiltersForm.restoreUiState(uiState);
    FacetFiltersForm.setLoading(false);
    document.dispatchEvent(new CustomEvent('cielo:collection-rendered', {
      detail: {
        sectionId: document.getElementById('product-grid')?.dataset.id || null,
        url: window.location.href,
      },
    }));
  }

  static renderProductGridContainer(parsedHTML) {
    const source = parsedHTML.getElementById('ProductGridContainer');
    const target = document.getElementById('ProductGridContainer');
    if (source && target) target.innerHTML = source.innerHTML;
  }

  static renderProductCount(parsedHTML) {
    const count = parsedHTML.getElementById('ProductCount')?.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (count === undefined || !container) return;
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(parsedHTML, event) {
    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const target = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
      if (target) target.innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender && event?.target instanceof Element) {
      const target = event.target.closest('.js-filter');
      if (target) FacetFiltersForm.renderCounts(countsToRender, target);
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const target = document.querySelector(selector);
      if (target) target.innerHTML = activeFacetsElement.innerHTML;
    })

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];
    mobileElementSelectors.forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (source && target) target.innerHTML = source.innerHTML;
    });
    FacetFiltersForm.positionFilterColumn();
    document.getElementById('FacetFiltersFormMobile')
      ?.closest('menu-drawer')
      ?.bindEvents();
  }

  static positionFilterColumn() {
    const column = document.querySelector('.product-filter-column');
    if (!column) return;
    const target = window.innerWidth < 768
      ? document.querySelector('.product-heading-row .container .row')
      : document.querySelector('.product-list-row');
    if (target && column.parentElement !== target) target.prepend(column);
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  createSearchParams(form) {
    const searchParams = new URLSearchParams();
    for (const [key, rawValue] of new FormData(form).entries()) {
      const value = String(rawValue).trim();
      if (value) searchParams.append(key, value);
    }
    return FacetFiltersForm.normalizeSearchParams(searchParams.toString(), form);
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const target = event.target instanceof Element ? event.target : null;
    const sourceForm = target?.closest('form');
    if (!sourceForm) return;
    const forms = [];
    const isMobile = sourceForm.id === 'FacetFiltersFormMobile';

    sortFilterForms.forEach((form) => {
      if (!isMobile) {
        if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
          forms.push(this.createSearchParams(form));
        }
      } else if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersFormMobile') {
        // The visible sort control lives outside the mobile filter form.
        // Serialize both so applying a mobile facet never drops sort_by.
        forms.push(this.createSearchParams(form));
      }
    });
    this.onSubmitForm(forms.filter(Boolean).join('&'), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.requestController = null;
FacetFiltersForm.requestSequence = 0;
FacetFiltersForm.historyListenerSet = false;
FacetFiltersForm.searchParamsInitial = FacetFiltersForm.normalizeSearchParams(window.location.search.slice(1));
FacetFiltersForm.searchParamsPrev = FacetFiltersForm.searchParamsInitial;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
